import { useState } from 'react'
import type { ReactElement } from 'react'

import type { WebhookBlock, WebhookImageBlock } from '@/api/types'
import { renderMarkdown } from '@/lib/markdown'
import { useExternalImages } from '@/lib/externalImages'
import styles from './WebhookBlocks.module.css'

function safeHttpUrl(value: string): string | null {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null
  } catch {
    return null
  }
}

interface ImageBlockProps {
  block: WebhookImageBlock
}

function ImageBlock({ block }: ImageBlockProps): ReactElement | null {
  const allowExternal = useExternalImages()
  const [approvedUrl, setApprovedUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const url = safeHttpUrl(block.url)
  if (url === null || failed) return null
  const external = new URL(url).origin !== window.location.origin
  if (external && !allowExternal && approvedUrl !== url) return (
    <div className={styles.imageConsent}>
      <p>External image: {block.alt || new URL(url).hostname}. Loading shares your IP address with {new URL(url).hostname}.</p>
      <button type="button" onClick={() => setApprovedUrl(url)}>Load external image</button>
    </div>
  )

  return (
    <img
      className={styles.image}
      src={url}
      alt={block.alt}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  )
}

export interface WebhookBlocksProps {
  blocks: readonly WebhookBlock[]
  currentUsername?: string | null
  mentionsEveryone?: boolean
  keyPrefix: string
}

export function WebhookBlocks({
  blocks,
  currentUsername = null,
  mentionsEveryone = false,
  keyPrefix,
}: WebhookBlocksProps): ReactElement {
  return (
    <div className={styles.blocks}>
      {blocks.map((block, index) => {
        const key = `${keyPrefix}-${index}`

        switch (block.type) {
          case 'title':
            return (
              <h3 className={styles.title} key={key}>
                {block.text}
              </h3>
            )

          case 'text':
            return (
              <div className={styles.text} key={key}>
                {renderMarkdown(block.text, {
                  currentUsername,
                  mentionsEveryone,
                  keyPrefix: key,
                })}
              </div>
            )

          case 'image':
            return <ImageBlock block={block} key={key} />

          case 'table':
            return (
              <div className={styles.tableScroll} key={key} role="region" aria-label="Webhook table" tabIndex={0}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      {block.columns.map((column, columnIndex) => (
                        <th key={`${key}-heading-${columnIndex}`} scope="col">
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, rowIndex) => (
                      <tr key={`${key}-row-${rowIndex}`}>
                        {row.map((cell, columnIndex) => (
                          <td key={`${key}-cell-${rowIndex}-${columnIndex}`}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )

          case 'actions':
            return (
              <div className={styles.actions} key={key}>
                {block.buttons.map((button, buttonIndex) => {
                  const url = safeHttpUrl(button.url)
                  if (url === null) return null
                  return (
                    <a
                      className={styles.button}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      key={`${key}-button-${buttonIndex}`}
                    >
                      {button.text}
                    </a>
                  )
                })}
              </div>
            )
        }
      })}
    </div>
  )
}

export default WebhookBlocks
